import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  Chip,
  Grid,
  Tooltip,
  Avatar,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import { User, CreateUserRequest, UpdateUserRequest, ChangePasswordRequest, UserRole } from '../models/User';
import { Hotel } from '../models/Hotel';
import * as userService from '../services/userService';
import * as hotelService from '../services/hotelService';
import * as firebaseService from '../services/firebaseService';
import { useAuth } from '../hooks/useAuth';
import UserForm from '../components/Users/UserForm';
import EditUserModal from '../components/Users/EditUserModal';
import ChangePasswordModal from '../components/Users/ChangePasswordModal';
import { registerUserWithFirebase } from '../services/authService';

export const Users = () => {
  const { user: currentUser, isHotelManager, userRole } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [firebaseHotels, setFirebaseHotels] = useState<firebaseService.FirebaseHotel[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; user: User | null }>({ open: false, user: null });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        if (userRole === UserRole.HotelManager && currentUser?.hotelId) {
          const usersData = await userService.getUsersByHotel(currentUser.hotelId);
          setUsers(usersData);
          try {
            const hotel = await hotelService.getHotelById(currentUser.hotelId);
            setHotels([hotel]);
            const fbHotel = await firebaseService.getFirebaseHotelByBackendId(currentUser.hotelId).catch(() => null);
            if (fbHotel) {
              setFirebaseHotels([fbHotel]);
            } else {
              const syncedHotel = await firebaseService.syncHotelWithFirebase(hotel);
              setFirebaseHotels([syncedHotel]);
            }
          } catch (error) {
            console.error("Error fetching hotel details:", error);
          }
        } else {
          setUsers([]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userRole, currentUser?.hotelId]);

  const findFirebaseHotelId = (backendHotelId: number): string | null => {
    const exactMatch = firebaseHotels.find(fbHotel => fbHotel.hotelInstanceId === backendHotelId);
    if (exactMatch?.id) return exactMatch.id;
    const backendHotel = hotels.find(h => h.id === backendHotelId);
    if (!backendHotel) return null;
    const matchingHotel = firebaseHotels.find(fbHotel =>
      fbHotel.name === backendHotel.name &&
      fbHotel.serverUrl === backendHotel.serverUrl &&
      fbHotel.serverPort === backendHotel.serverPort
    );
    return matchingHotel?.id || null;
  };

  const handleCreateUser = async (userData: CreateUserRequest) => {
    try {
      setSubmitError('');
      const createdUser = await userService.createUser(userData);
      setUsers(prev => [...prev, createdUser]);
      if (userData.hotelId) {
        const firebaseHotelId = findFirebaseHotelId(userData.hotelId);
        if (firebaseHotelId) {
          try {
            await registerUserWithFirebase(userData.username, firebaseHotelId, userData.hotelId, userData.role);
          } catch (error) {
            console.error("Error registering user with Firebase:", error);
          }
        } else {
          try {
            const hotel = await hotelService.getHotelById(userData.hotelId);
            const syncedHotel = await firebaseService.syncHotelWithFirebase(hotel);
            if (syncedHotel?.id) {
              await registerUserWithFirebase(userData.username, syncedHotel.id, userData.hotelId, userData.role);
            }
          } catch (error) {
            console.error("Error syncing hotel for user registration:", error);
          }
        }
      }
      setCreateDialogOpen(false);
    } catch (error: any) {
      console.error('Error creating user:', error);
      if (typeof error.response?.data === 'string') {
        setSubmitError(error.response.data);
      } else if (error.response?.data?.title) {
        setSubmitError(error.response.data.title);
      } else if (error.message) {
        setSubmitError(error.message);
      } else {
        setSubmitError('Kullanıcı oluşturulamadı. Lütfen tekrar deneyin.');
      }
      throw error;
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteConfirm.user) return;
    try {
      await userService.deleteUser(deleteConfirm.user.id);
      setUsers(users.filter(u => u.id !== deleteConfirm.user!.id));
      setDeleteConfirm({ open: false, user: null });
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const handleEditUser = async (userId: number, userData: UpdateUserRequest) => {
    try {
      const updatedUser = await userService.updateUser(userId, userData);
      setUsers(users.map(u => u.id === userId ? updatedUser : u));
      setEditDialogOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  };

  const handleChangePassword = async (userId: number, passwordData: ChangePasswordRequest) => {
    try {
      await userService.changePassword(userId, passwordData);
      setChangePasswordDialogOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  };

  const getRoleColor = (role: UserRole) => {
    return role === UserRole.HotelManager ? '#7b1fa2' : '#4294e3';
  };

  const getRoleLabel = (role: UserRole) => {
    return role === UserRole.HotelManager ? 'Manager' : 'Staff';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} sx={{ color: '#4294e3' }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#1a1a2e' }}>
          Kullanıcılar
        </Typography>
        <Button
          startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={() => { setSubmitError(''); setCreateDialogOpen(true); }}
          sx={{
            textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
            borderRadius: '10px', px: 2.5, py: 0.8,
            backgroundColor: '#4294e3', color: '#fff',
            '&:hover': { backgroundColor: '#3680c9' }
          }}
        >
          Kullanıcı Ekle
        </Button>
      </Box>

      {/* User Cards */}
      <Grid container spacing={2}>
        {users.length === 0 ? (
          <Grid item xs={12}>
            <Box sx={{
              py: 6, textAlign: 'center',
              borderRadius: '14px', border: '2px dashed #e8e8e8',
              backgroundColor: '#fafafa'
            }}>
              <PersonRoundedIcon sx={{ fontSize: 40, color: '#ccc', mb: 1.5 }} />
              <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#888', mb: 0.5 }}>
                Kullanıcı Yok
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: '#aaa', mb: 2.5 }}>
                Henüz kullanıcı oluşturulmamış
              </Typography>
              <Button
                startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                onClick={() => { setSubmitError(''); setCreateDialogOpen(true); }}
                sx={{
                  textTransform: 'none', fontWeight: 600, fontSize: '0.85rem',
                  borderRadius: '10px', px: 3, py: 0.8,
                  backgroundColor: '#4294e3', color: '#fff',
                  '&:hover': { backgroundColor: '#3680c9' }
                }}
              >
                Kullanıcı Oluştur
              </Button>
            </Box>
          </Grid>
        ) : (
          users.map(user => (
            <Grid item xs={12} sm={6} lg={4} key={user.id}>
              <Box sx={{
                p: 2.5, borderRadius: '14px',
                border: '1px solid #f0f0f0',
                backgroundColor: '#fff',
                transition: 'all 0.2s',
                '&:hover': { borderColor: '#e0e0e0', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }
              }}>
                {/* Top: Avatar + Name + Role */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Avatar sx={{
                    width: 40, height: 40,
                    backgroundColor: `${getRoleColor(user.role)}12`,
                    color: getRoleColor(user.role),
                    fontSize: '0.95rem', fontWeight: 700,
                  }}>
                    {user.username.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontWeight: 600, fontSize: '0.9rem', color: '#1a1a2e',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                    }}>
                      {user.username}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        icon={user.role === UserRole.HotelManager
                          ? <AdminPanelSettingsRoundedIcon sx={{ fontSize: 12 }} />
                          : <PersonRoundedIcon sx={{ fontSize: 12 }} />}
                        label={getRoleLabel(user.role)}
                        size="small"
                        sx={{
                          height: 20, fontSize: '0.6rem', fontWeight: 600,
                          borderRadius: '6px',
                          backgroundColor: `${getRoleColor(user.role)}12`,
                          color: getRoleColor(user.role),
                          '& .MuiChip-icon': { color: getRoleColor(user.role) }
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Actions */}
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                  gap: 0.5, pt: 1.5, borderTop: '1px solid #f5f5f5'
                }}>
                  <Tooltip title="Düzenle">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => { setSelectedUser(user); setEditDialogOpen(true); }}
                        disabled={user.role === UserRole.HotelManager && userRole !== UserRole.HotelManager}
                        sx={{ color: '#888', '&:hover': { color: '#4294e3' } }}
                      >
                        <EditRoundedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Şifre Değiştir">
                    <IconButton
                      size="small"
                      onClick={() => { setSelectedUser(user); setChangePasswordDialogOpen(true); }}
                      sx={{ color: '#888', '&:hover': { color: '#f57c00' } }}
                    >
                      <VpnKeyRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={
                    user.role === UserRole.HotelManager ? 'Manager silinemez' :
                    user.id === currentUser?.id ? 'Kendinizi silemezsiniz' : 'Sil'
                  }>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteConfirm({ open: true, user })}
                        disabled={user.role === UserRole.HotelManager || user.id === currentUser?.id}
                        sx={{ color: '#bbb', '&:hover': { color: '#c62828' } }}
                      >
                        <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </Grid>
          ))
        )}
      </Grid>

      {/* Create User Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden',
            maxHeight: '85vh'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          backgroundColor: '#fff', borderBottom: '1px solid #f0f0f0',
          py: 2, px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '10px',
              backgroundColor: 'rgba(66, 148, 227, 0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <PersonAddRoundedIcon sx={{ fontSize: 18, color: '#4294e3' }} />
            </Box>
            <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#1a1a2e' }}>
              Yeni Kullanıcı Oluştur
            </Typography>
          </Box>
          <IconButton onClick={() => setCreateDialogOpen(false)} size="small"
            sx={{ width: 32, height: 32, color: '#999', '&:hover': { color: '#666', backgroundColor: 'rgba(0,0,0,0.04)' } }}>
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {submitError && (
            <Alert
              severity="error"
              sx={{
                mb: 2, borderRadius: '10px',
                backgroundColor: 'rgba(229, 57, 53, 0.05)',
                border: '1px solid rgba(229, 57, 53, 0.15)',
              }}
            >
              {submitError}
            </Alert>
          )}
          <UserForm onSubmit={handleCreateUser} />
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <EditUserModal
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setSelectedUser(null); }}
        user={selectedUser}
        onSubmit={handleEditUser}
      />

      {/* Change Password Dialog */}
      <ChangePasswordModal
        open={changePasswordDialogOpen}
        onClose={() => { setChangePasswordDialogOpen(false); setSelectedUser(null); }}
        user={selectedUser}
        onSubmit={handleChangePassword}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, user: null })}
        PaperProps={{ sx: { borderRadius: '16px', maxWidth: 380, mx: 'auto', overflow: 'hidden' } }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '14px', mx: 'auto', mb: 2,
            backgroundColor: 'rgba(229, 57, 53, 0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <WarningAmberRoundedIcon sx={{ fontSize: 24, color: '#c62828' }} />
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#1a1a2e', mb: 0.5 }}>
            Kullanıcıyı Sil
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#888', mb: 2.5, lineHeight: 1.5 }}>
            <strong style={{ color: '#1a1a2e' }}>"{deleteConfirm.user?.username}"</strong> kullanıcısını silmek istiyor musunuz? Bu işlem geri alınamaz.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              onClick={() => setDeleteConfirm({ open: false, user: null })}
              sx={{
                textTransform: 'none', fontWeight: 500, borderRadius: '10px',
                color: '#666', backgroundColor: '#f5f5f5', py: 1,
                '&:hover': { backgroundColor: '#eee' }
              }}
            >
              Vazgeç
            </Button>
            <Button
              fullWidth
              onClick={handleDeleteUser}
              sx={{
                textTransform: 'none', fontWeight: 600, borderRadius: '10px',
                backgroundColor: '#c62828', color: '#fff', py: 1,
                '&:hover': { backgroundColor: '#b71c1c' }
              }}
            >
              Sil
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};
